import { config } from '../config.ts';
import { createStorageTexture2D, createUniformBuffer } from '../gpu/buffers.ts';
import { formats } from '../gpu/formats.ts';
import { createComputePipeline } from '../gpu/pipelines.ts';
import type { HeightmapModule } from './heightmap.ts';
import wgsl from './erosion.wgsl?raw';

/**
 * Hydraulic erosion via grid-pipes (Mei et al. 2007).
 * Each iteration runs 4 sub-passes (flux → water → erode → transport).
 * After all iterations, a finalize pass deposits remaining sediment back into terrain.
 *
 * Bind groups for both "even" and "odd" iteration configurations are built up-front
 * (8 bind groups, 4 per config), and the whole loop is recorded into a single command
 * encoder + queue.submit — historically this ran 150 separate submits, which spent
 * most of generation time in CPU/GPU synchronization rather than actual erosion work.
 */
export async function runErosion(
  device: GPUDevice,
  source: HeightmapModule,
  opts: { seed: number; iterations: number },
): Promise<HeightmapModule> {
  // The finalize pass below assumes the "current" state ends up in the *A slots,
  // which only holds after an even number of iterations — fail before allocating.
  if ((opts.iterations & 1) !== 0) {
    throw new Error(`erosion expects even iterations; got ${opts.iterations}`);
  }
  const size = config.heightmapSize;
  const dim: readonly [number, number] = [size, size];

  // --- Allocate ping-pong textures. ---
  const terrA = createStorageTexture2D(device, dim, formats.heightStorage, 'erosion.terrA');
  const terrB = createStorageTexture2D(device, dim, formats.heightStorage, 'erosion.terrB');
  const waterA = createStorageTexture2D(device, dim, formats.heightStorage, 'erosion.waterA');
  const waterB = createStorageTexture2D(device, dim, formats.heightStorage, 'erosion.waterB');
  const fluxA = createStorageTexture2D(device, dim, formats.fluxStorage, 'erosion.fluxA');
  const fluxB = createStorageTexture2D(device, dim, formats.fluxStorage, 'erosion.fluxB');
  const sedA = createStorageTexture2D(device, dim, formats.heightStorage, 'erosion.sedA');
  const sedB = createStorageTexture2D(device, dim, formats.heightStorage, 'erosion.sedB');
  const velA = createStorageTexture2D(device, dim, formats.fluxStorage, 'erosion.velA');
  const velB = createStorageTexture2D(device, dim, formats.fluxStorage, 'erosion.velB');

  const view = (t: GPUTexture, label: string) => t.createView({ label });
  const v = {
    terrA: view(terrA, 'erosion.terrA.view'),
    terrB: view(terrB, 'erosion.terrB.view'),
    waterA: view(waterA, 'erosion.waterA.view'),
    waterB: view(waterB, 'erosion.waterB.view'),
    fluxA: view(fluxA, 'erosion.fluxA.view'),
    fluxB: view(fluxB, 'erosion.fluxB.view'),
    sedA: view(sedA, 'erosion.sedA.view'),
    sedB: view(sedB, 'erosion.sedB.view'),
    velA: view(velA, 'erosion.velA.view'),
    velB: view(velB, 'erosion.velB.view'),
  };

  // --- Uniform params (12 f32s + 1 u32 = 52 bytes; round to 64). ---
  // Tuned for terrain heights in [0,1] (not the meters-scale assumed in Mei et al.).
  // The big lever is gravity ~20× smaller — keeps per-step flux on the same order
  // of magnitude as the height differences it drives.
  const paramsBuffer = createUniformBuffer(device, 64, 'erosion.params');
  const dt = 0.05;
  const rainfall = 0.001;
  const pipeArea = 1.0;
  const pipeLength = 1.0;
  const gravity = 0.5;
  const kCapacity = 0.05;
  const kDissolution = 0.05;
  const kDeposition = 0.1;
  const kEvap = 0.02;
  const cellArea = 1.0;
  const cellLength = 1.0;

  const fParams = new Float32Array([
    dt, rainfall, pipeArea, pipeLength,
    gravity, kCapacity, kDissolution, kDeposition,
    kEvap, cellArea, cellLength, 0,
  ]);
  device.queue.writeBuffer(paramsBuffer, 0, fParams.buffer, fParams.byteOffset, fParams.byteLength);
  const uSeed = new Uint32Array([opts.seed >>> 0]);
  device.queue.writeBuffer(paramsBuffer, 11 * 4, uSeed.buffer, uSeed.byteOffset, uSeed.byteLength);

  // --- Pipelines (one per entry point; each gets its own auto layout). ---
  const pipe1 = createComputePipeline(device, wgsl, 'pass1_flux', 'auto', 'erosion.pass1');
  const pipe2 = createComputePipeline(device, wgsl, 'pass2_water', 'auto', 'erosion.pass2');
  const pipe3 = createComputePipeline(device, wgsl, 'pass3_erode', 'auto', 'erosion.pass3');
  const pipe4 = createComputePipeline(device, wgsl, 'pass4_transport', 'auto', 'erosion.pass4');
  const pipeF = createComputePipeline(device, wgsl, 'pass_finalize', 'auto', 'erosion.passFinalize');

  // --- Bind groups, built once for both configurations. ---
  //
  // Sed is special: pass4 writes back to its "cur" slot rather than ping-ponging,
  // so curSed always lives in sedA regardless of iteration parity. The other four
  // resources (terr, water, flux, vel) flip between A and B each iteration.
  //
  //   Even iter (cur* = *A):  bg1..4 read A and write B (except sed: read B then back to A).
  //   Odd iter  (terr/water/flux/vel: cur=B, alt=A; sed: cur=A): mirrored.

  // Per-iteration resource roles. `cur` reads come from the "current" texture,
  // `alt` writes go to the alternate. Sed is asymmetric: pass4 writes back to cur
  // (not alt), so cur stays in sedA every iteration. Vel is only ever read/written
  // through `alt` — pass1 doesn't touch vel and pass4 doesn't either, so the
  // helper omits curVel entirely.
  interface ErosionRoles {
    terr: { cur: GPUTextureView; alt: GPUTextureView };
    water: { cur: GPUTextureView; alt: GPUTextureView };
    flux: { cur: GPUTextureView; alt: GPUTextureView };
    sed: { cur: GPUTextureView; alt: GPUTextureView };
    vel: { alt: GPUTextureView };
  }

  const makeBindGroups = (roles: ErosionRoles, label: string) => ({
    bg1: device.createBindGroup({
      label: `erosion.${label}.pass1.bg`,
      layout: pipe1.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: roles.terr.cur },
        { binding: 1, resource: roles.water.cur },
        { binding: 2, resource: roles.flux.cur },
        { binding: 3, resource: roles.water.alt },
        { binding: 4, resource: roles.flux.alt },
        { binding: 5, resource: { buffer: paramsBuffer } },
      ],
    }),
    bg2: device.createBindGroup({
      label: `erosion.${label}.pass2.bg`,
      layout: pipe2.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: roles.water.alt },
        { binding: 1, resource: roles.flux.alt },
        { binding: 2, resource: roles.water.cur },
        { binding: 3, resource: roles.vel.alt },
        { binding: 4, resource: { buffer: paramsBuffer } },
      ],
    }),
    bg3: device.createBindGroup({
      label: `erosion.${label}.pass3.bg`,
      layout: pipe3.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: roles.terr.cur },
        { binding: 1, resource: roles.sed.cur },
        { binding: 2, resource: roles.vel.alt },
        { binding: 3, resource: roles.terr.alt },
        { binding: 4, resource: roles.sed.alt },
        { binding: 5, resource: { buffer: paramsBuffer } },
      ],
    }),
    bg4: device.createBindGroup({
      label: `erosion.${label}.pass4.bg`,
      layout: pipe4.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: roles.sed.alt },
        { binding: 1, resource: roles.vel.alt },
        { binding: 2, resource: roles.water.cur },
        { binding: 3, resource: roles.sed.cur },
        { binding: 4, resource: roles.water.alt },
        { binding: 5, resource: { buffer: paramsBuffer } },
      ],
    }),
  });

  const cfgEven = makeBindGroups({
    terr:  { cur: v.terrA,  alt: v.terrB },
    water: { cur: v.waterA, alt: v.waterB },
    flux:  { cur: v.fluxA,  alt: v.fluxB },
    sed:   { cur: v.sedA,   alt: v.sedB },
    vel:   { alt: v.velB },
  }, 'even');
  const cfgOdd = makeBindGroups({
    terr:  { cur: v.terrB,  alt: v.terrA },
    water: { cur: v.waterB, alt: v.waterA },
    flux:  { cur: v.fluxB,  alt: v.fluxA },
    sed:   { cur: v.sedA,   alt: v.sedB },   // sed never flips
    vel:   { alt: v.velA },
  }, 'odd');

  const dispatchSize = Math.ceil(size / 8);

  // --- Single command encoder for init copy + all iterations + finalize. ---
  const encoder = device.createCommandEncoder({ label: 'erosion' });

  // Init: copy source heightmap into terrA.
  encoder.copyTextureToTexture(
    { texture: source.texture },
    { texture: terrA },
    { width: size, height: size },
  );

  for (let i = 0; i < opts.iterations; i++) {
    const cfg = (i & 1) === 0 ? cfgEven : cfgOdd;
    {
      const p = encoder.beginComputePass({ label: 'erosion.pass1' });
      p.setPipeline(pipe1); p.setBindGroup(0, cfg.bg1);
      p.dispatchWorkgroups(dispatchSize, dispatchSize, 1);
      p.end();
    }
    {
      const p = encoder.beginComputePass({ label: 'erosion.pass2' });
      p.setPipeline(pipe2); p.setBindGroup(0, cfg.bg2);
      p.dispatchWorkgroups(dispatchSize, dispatchSize, 1);
      p.end();
    }
    {
      const p = encoder.beginComputePass({ label: 'erosion.pass3' });
      p.setPipeline(pipe3); p.setBindGroup(0, cfg.bg3);
      p.dispatchWorkgroups(dispatchSize, dispatchSize, 1);
      p.end();
    }
    {
      const p = encoder.beginComputePass({ label: 'erosion.pass4' });
      p.setPipeline(pipe4); p.setBindGroup(0, cfg.bg4);
      p.dispatchWorkgroups(dispatchSize, dispatchSize, 1);
      p.end();
    }
  }

  // --- Finalize: deposit remaining sediment into terrain.
  // After an even number of iterations (asserted above) the "current" state is in
  // *A for all resources, so reads come from sedA + terrA, writes to terrB.
  const finalSrcTerr = v.terrA;
  const finalSrcSed = v.sedA;
  const finalDestView = v.terrB;
  const finalDestTexture = terrB;
  const finalBg = device.createBindGroup({
    label: 'erosion.finalize.bg',
    layout: pipeF.getBindGroupLayout(0),
    entries: [
      { binding: 0, resource: finalSrcTerr },
      { binding: 1, resource: finalSrcSed },
      { binding: 2, resource: finalDestView },
    ],
  });
  {
    const p = encoder.beginComputePass({ label: 'erosion.finalize.pass' });
    p.setPipeline(pipeF);
    p.setBindGroup(0, finalBg);
    p.dispatchWorkgroups(dispatchSize, dispatchSize, 1);
    p.end();
  }

  device.queue.submit([encoder.finish()]);

  source.destroy();
  terrA.destroy();
  waterA.destroy();
  waterB.destroy();
  fluxA.destroy();
  fluxB.destroy();
  sedA.destroy();
  sedB.destroy();
  velA.destroy();
  velB.destroy();
  paramsBuffer.destroy();

  return {
    texture: finalDestTexture,
    view: finalDestView,
    destroy() {
      finalDestTexture.destroy();
    },
  };
}
